import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { Box, InputLabel, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { SegmentedToggle } from '@liminal/web-ui/forms/SegmentedToggle';
import { Input } from '@liminal/web-ui/forms/Input';
import { Select } from '@liminal/web-ui/forms/Select';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { adminClient } from '@/lib/adminClient';
import { useDebounce } from '@/hooks/useDebounce';
import type { AdminAudience } from '@/api/types';
import { AudienceKind } from '@/api/generated';

type AudienceMode = 'all' | 'list' | 'filters';

// Mirrors the Persona-scoped filter values on the users page — the audience
// resolution runs through the same enrichment pipeline (fetchAndEnrichUsers).
const KYC_STATUS_OPTIONS = [
  { value: '', label: 'All Persona Statuses' },
  { value: 'KYC_STATUS_VERIFIED', label: 'Approved' },
  { value: 'KYC_STATUS_PENDING', label: 'Pending' },
  { value: 'KYC_STATUS_UNDER_REVIEW', label: 'Under Review' },
  { value: 'KYC_STATUS_NEEDS_INFO', label: 'Needs Info' },
  { value: 'KYC_STATUS_REJECTED', label: 'Rejected' },
  { value: 'KYC_STATUS_SUSPENDED', label: 'Suspended' },
  { value: 'KYC_STATUS_UNSPECIFIED', label: 'Not Started' },
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All Platforms' },
  { value: 'PLATFORM_IOS', label: 'iOS' },
  { value: 'PLATFORM_ANDROID', label: 'Android' },
  { value: 'PLATFORM_WEB', label: 'Web' },
];

export interface AudienceSelection {
  /** Request payload; undefined = all users. */
  audience?: AdminAudience;
  /** Gate for the form submit — false while a specific selection is incomplete. */
  valid: boolean;
  /** Why the selection is invalid, for the form error banner. */
  error?: string;
  /** Human summary for confirm copy, e.g. "all users" or "12 selected users". */
  summary: string;
}

interface AudienceSelectorProps {
  onChange: (selection: AudienceSelection) => void;
  disabled?: boolean;
}

function parseIdentifiers(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Shared audience picker for announcement forms: all users, a pasted list of
 * user ids/emails (resolved live so typos surface before sending), or the
 * users-page filters (with a live recipient count). The parent receives the
 * request payload plus a validity gate via `onChange`.
 */
export function AudienceSelector({ onChange, disabled }: AudienceSelectorProps) {
  const [mode, setMode] = useState<AudienceMode>('all');
  const [rawList, setRawList] = useState('');
  const [query, setQuery] = useState('');
  const [kycStatus, setKycStatus] = useState('');
  const [platform, setPlatform] = useState('');

  // The free-text query commits on Enter/blur rather than as-you-type: each
  // count is a full user-base sweep server-side, so it must not fire per
  // keystroke. The selects commit immediately (discrete, occasional changes).
  const [committedQuery, setCommittedQuery] = useState('');

  const debouncedList = useDebounce(rawList, 500);

  const identifiers = useMemo(() => parseIdentifiers(debouncedList), [debouncedList]);

  const resolveQuery = useQuery({
    queryKey: ['audience-resolve-users', identifiers],
    queryFn: () => adminClient.resolveUsers(identifiers),
    enabled: mode === 'list' && identifiers.length > 0,
  });

  const countQuery = useQuery({
    queryKey: ['audience-filter-count', committedQuery, kycStatus, platform],
    queryFn: () =>
      adminClient.searchUsers({
        query: committedQuery,
        kycStatusFilter: kycStatus,
        platformFilter: platform,
        limit: 1,
        offset: 0,
      }),
    enabled: mode === 'filters',
  });

  const matchedIds = useMemo(
    () =>
      (resolveQuery.data?.profiles ?? [])
        .map((p) => p.userId)
        .filter((id): id is string => !!id),
    [resolveQuery.data],
  );
  const unmatched = useMemo(() => resolveQuery.data?.unmatched ?? [], [resolveQuery.data]);
  const filterCount = countQuery.data?.totalCount;

  const listPending = rawList !== debouncedList || resolveQuery.isFetching;
  const queryUncommitted = query.trim() !== committedQuery;
  const filtersPending = countQuery.isFetching;

  const selection = useMemo<AudienceSelection>(() => {
    switch (mode) {
      case 'all':
        return { valid: true, summary: 'all users' };
      case 'list': {
        if (parseIdentifiers(rawList).length === 0) {
          return { valid: false, error: 'Paste at least one user id or email', summary: 'no users selected' };
        }
        if (listPending) {
          return { valid: false, error: 'Resolving users…', summary: 'resolving users' };
        }
        if (resolveQuery.isError) {
          return { valid: false, error: 'Failed to resolve users — try again', summary: 'no users selected' };
        }
        if (unmatched.length > 0) {
          return {
            valid: false,
            error: `Unmatched: ${unmatched.join(', ')}`,
            summary: 'no users selected',
          };
        }
        if (matchedIds.length === 0) {
          return { valid: false, error: 'No users matched the pasted list', summary: 'no users selected' };
        }
        return {
          audience: { kind: AudienceKind.AUDIENCE_KIND_USER_IDS, userIds: matchedIds },
          valid: true,
          summary: `${matchedIds.length} selected ${matchedIds.length === 1 ? 'user' : 'users'}`,
        };
      }
      case 'filters': {
        if (queryUncommitted) {
          return { valid: false, error: 'Press Enter to apply the search text', summary: 'no users selected' };
        }
        if (filtersPending || filterCount === undefined) {
          return { valid: false, error: 'Counting matching users…', summary: 'counting users' };
        }
        if (countQuery.isError) {
          return { valid: false, error: 'Failed to count matching users — try again', summary: 'no users selected' };
        }
        if (filterCount === 0) {
          return { valid: false, error: 'The filters match 0 users', summary: 'no users selected' };
        }
        return {
          audience: {
            kind: AudienceKind.AUDIENCE_KIND_FILTERS,
            filters: {
              ...(committedQuery && { query: committedQuery }),
              ...(kycStatus && { kycStatusFilter: kycStatus }),
              ...(platform && { platformFilter: platform }),
            },
          },
          valid: true,
          summary: `${filterCount} ${filterCount === 1 ? 'user' : 'users'} matching the filters`,
        };
      }
    }
  }, [
    mode,
    rawList,
    listPending,
    resolveQuery.isError,
    unmatched,
    matchedIds,
    queryUncommitted,
    filtersPending,
    filterCount,
    countQuery.isError,
    committedQuery,
    kycStatus,
    platform,
  ]);

  // Reported when the selection changes, not when `onChange` does: the dialogs
  // pass inline setters.
  const onSelected = useEffectEvent(onChange);
  useEffect(() => {
    onSelected(selection);
  }, [selection]);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Same label chrome as the library's form fields (Field renders an
          InputLabel too), so "Audience" doesn't read differently from the
          Search/KYC/Platform labels below it. */}
      <InputLabel>Audience</InputLabel>
      <SegmentedToggle
        aria-label="Audience"
        size="sm"
        value={mode}
        onChange={setMode}
        disabled={disabled}
        options={[
          { value: 'all', label: 'All users' },
          { value: 'list', label: 'User list' },
          { value: 'filters', label: 'Filters' },
        ]}
      />

      {mode === 'list' && (
        <Box sx={{ mt: 1.5 }}>
          <Textarea
            label="User IDs or emails"
            value={rawList}
            onChange={setRawList}
            rows={3}
            placeholder={'One per line (or comma-separated):\nfaiz@liminal.cash\n550e8400-e29b-41d4-a716-446655440000'}
            helper={
              listPending
                ? 'Resolving…'
                : resolveQuery.data
                  ? `${matchedIds.length} matched${unmatched.length > 0 ? `, ${unmatched.length} unmatched` : ''}`
                  : 'Resolved against user profiles before sending'
            }
          />
          {unmatched.length > 0 && (
            <Typography variant="caption" color="error" component="div">
              Unmatched: {unmatched.join(', ')}
            </Typography>
          )}
        </Box>
      )}

      {mode === 'filters' && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Input
                label="Search"
                size="sm"
                value={query}
                onChange={setQuery}
                onBlur={() => setCommittedQuery(query.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setCommittedQuery(query.trim());
                }}
                placeholder="Name, email, tag…"
                helper={queryUncommitted ? 'Press Enter to apply' : undefined}
              />
            </Box>
            <Box sx={{ width: 170 }}>
              <Select label="KYC Status" size="sm" value={kycStatus} onChange={setKycStatus} options={KYC_STATUS_OPTIONS} />
            </Box>
            <Box sx={{ width: 150 }}>
              <Select label="Platform" size="sm" value={platform} onChange={setPlatform} options={PLATFORM_OPTIONS} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {queryUncommitted
              ? 'Press Enter to update the count'
              : filtersPending
                ? 'Counting matching users…'
                : filterCount !== undefined
                  ? `${filterCount} matching ${filterCount === 1 ? 'user' : 'users'} right now — the list is frozen when you send`
                  : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
